import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ApartmentManager.css';

interface LineSetting {
  dong: string;
  line: string;
}

interface ApartmentConfig {
  apartment_name: string;
  apartment_id?: string;
  apartment_address?: string;
  entrance_password?: string;
  line_settings: LineSetting[];
  is_configured?: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

interface ApartmentSyncCandidate {
  apartment_name: string;
  admin_label?: string;
  carryhealcount_name?: string | null;
  apartment_id?: string;
  current_apartment_id?: string;
  apartment_address?: string;
  is_configured?: boolean;
  needs_sync?: boolean;
  needs_mapping_update?: boolean;
  resolution_source?: string;
  sync_source?: string;
  sync_warning?: string;
}

const emptyConfig = (apartmentName = ''): ApartmentConfig => ({
  apartment_name: apartmentName,
  apartment_id: '',
  apartment_address: '',
  entrance_password: '',
  line_settings: [{ dong: '', line: '' }],
  is_configured: false,
});

const ApartmentManager: React.FC = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<ApartmentConfig[]>([]);
  const [selectedName, setSelectedName] = useState<string>('');
  const [form, setForm] = useState<ApartmentConfig>(emptyConfig());
  const [searchText, setSearchText] = useState('');
  const [newApartmentName, setNewApartmentName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncCandidates, setSyncCandidates] = useState<ApartmentSyncCandidate[]>([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const getApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    if (window.location.protocol === 'https:') {
      return window.location.origin;
    }
    return `http://${hostname}:8000`;
  };

  const setStatus = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage(text);
    setMessageType(type);
  };

  const token = localStorage.getItem('token');

  const fetchSyncPreview = async () => {
    if (!token) return;
    setSyncLoading(true);
    try {
      const response = await fetch(`${getApiBase()}/api/admin/apartment-configs/sync-preview`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('외부 아파트 동기화 미리보기 조회 실패');
      }

      const data = await response.json();
      setSyncCandidates(data.pending_candidates || []);
    } catch (err) {
      console.error(err);
      setSyncCandidates([]);
    } finally {
      setSyncLoading(false);
    }
  };

  const fetchConfigs = async (preferName?: string) => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${getApiBase()}/api/admin/apartment-configs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }
      if (response.status === 403) {
        setStatus('관리자 권한이 필요합니다.', 'error');
        return;
      }
      if (!response.ok) {
        throw new Error('아파트 설정 목록 조회 실패');
      }

      const data: ApartmentConfig[] = await response.json();
      setConfigs(data);

      const nextName = preferName || selectedName;
      const picked = data.find((item) => item.apartment_name === nextName) || data[0];

      if (picked) {
        setSelectedName(picked.apartment_name);
        setForm({
          ...picked,
          apartment_id: picked.apartment_id || '',
          apartment_address: picked.apartment_address || '',
          entrance_password: picked.entrance_password || '',
          line_settings: picked.line_settings?.length ? picked.line_settings : [{ dong: '', line: '' }],
        });
      } else {
        setSelectedName('');
        setForm(emptyConfig());
      }
    } catch (err) {
      console.error(err);
      setStatus('아파트 설정 목록을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
      fetchSyncPreview();
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const filteredConfigs = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return configs;
    return configs.filter((item) => item.apartment_name.toLowerCase().includes(q));
  }, [configs, searchText]);

  const selectApartment = (name: string) => {
    setSelectedName(name);
    const found = configs.find((cfg) => cfg.apartment_name === name);
    if (!found) {
      setForm(emptyConfig(name));
      return;
    }
    setForm({
      ...found,
      apartment_id: found.apartment_id || '',
      apartment_address: found.apartment_address || '',
      entrance_password: found.entrance_password || '',
      line_settings: found.line_settings?.length ? found.line_settings : [{ dong: '', line: '' }],
    });
  };

  const addApartmentDraft = () => {
    const name = newApartmentName.trim();
    if (!name) return;

    const exists = configs.find((item) => item.apartment_name === name);
    if (exists) {
      selectApartment(name);
      setNewApartmentName('');
      setStatus(`'${name}' 아파트가 이미 존재합니다.`);
      return;
    }

    const draft = emptyConfig(name);
    setConfigs((prev) => [draft, ...prev]);
    setSelectedName(name);
    setForm(draft);
    setNewApartmentName('');
    setStatus(`'${name}' 추가 준비 완료. 저장을 눌러 반영하세요.`);
  };

  const updateLine = (index: number, key: 'dong' | 'line', value: string) => {
    setForm((prev) => {
      const next = [...prev.line_settings];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, line_settings: next };
    });
  };

  const addLineRow = () => {
    setForm((prev) => ({
      ...prev,
      line_settings: [...prev.line_settings, { dong: '', line: '' }],
    }));
  };

  const removeLineRow = (index: number) => {
    setForm((prev) => {
      const next = prev.line_settings.filter((_, i) => i !== index);
      return {
        ...prev,
        line_settings: next.length ? next : [{ dong: '', line: '' }],
      };
    });
  };

  const saveConfig = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    const apartmentName = (form.apartment_name || '').trim();
    if (!apartmentName) {
      setStatus('아파트명은 필수입니다.', 'error');
      return;
    }

    const payload = {
      apartment_name: apartmentName,
      apartment_id: (form.apartment_id || '').trim(),
      apartment_address: (form.apartment_address || '').trim(),
      entrance_password: (form.entrance_password || '').trim(),
      line_settings: form.line_settings
        .map((row) => ({ dong: (row.dong || '').trim(), line: (row.line || '').trim() }))
        .filter((row) => row.dong && row.line),
    };

    setSaving(true);
    try {
      const response = await fetch(`${getApiBase()}/api/admin/apartment-configs`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '저장에 실패했습니다.');
      }

      setStatus(`'${apartmentName}' 설정 저장 완료`);
      await fetchConfigs(apartmentName);
    } catch (err: any) {
      setStatus(err?.message || '저장 중 오류가 발생했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    const target = selectedName || form.apartment_name;
    if (!target) return;
    if (!window.confirm(`'${target}' 아파트 설정을 삭제하시겠습니까?`)) return;

    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/apartment-configs/${encodeURIComponent(target)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '삭제에 실패했습니다.');
      }

      setStatus(`'${target}' 설정 삭제 완료`);
      await fetchConfigs();
    } catch (err: any) {
      setStatus(err?.message || '삭제 중 오류가 발생했습니다.', 'error');
    }
  };

  const loadCandidateIntoForm = (candidate: ApartmentSyncCandidate) => {
    const nextName = (candidate.apartment_name || '').trim();
    if (!nextName) return;
    setSelectedName(nextName);
    setForm({
      apartment_name: nextName,
      apartment_id: candidate.apartment_id || '',
      apartment_address: candidate.apartment_address || '',
      entrance_password: '',
      line_settings: [{ dong: '', line: '' }],
      is_configured: false,
    });
    setStatus(`'${nextName}' 후보를 편집 폼에 불러왔습니다.`);
  };

  const syncApartmentCandidates = async (apartmentNames?: string[]) => {
    if (!token) {
      navigate('/login');
      return;
    }

    setSyncing(true);
    try {
      const response = await fetch(`${getApiBase()}/api/admin/apartment-configs/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apartment_names: apartmentNames || [],
          fill_only_missing: true,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || '외부 아파트 동기화에 실패했습니다.');
      }

      const created = Number(data.created_count || 0);
      const updated = Number(data.updated_count || 0);
      const mappingUpdated = Number(data.mapping_update_count || 0);
      setStatus(`동기화 완료: 생성 ${created}건, 보정 ${updated}건, 매핑 ${mappingUpdated}건`);
      await fetchConfigs(apartmentNames?.[0]);
    } catch (err: any) {
      setStatus(err?.message || '외부 아파트 동기화 중 오류가 발생했습니다.', 'error');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="apartment-manager-page">
      <header className="apartment-manager-header">
        <button className="am-back-btn" onClick={() => navigate('/admin')}>
          ← 대시보드
        </button>
        <h1>아파트 선택 / 코드·라인 설정</h1>
        <button className="am-refresh-btn" onClick={() => fetchConfigs(selectedName)}>
          새로고침
        </button>
      </header>

      {message && (
        <div className={`am-message ${messageType === 'error' ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="apartment-manager-content">
        <aside className="am-list-panel">
          <div className="am-card">
            <h2>아파트 목록</h2>
            <div className="am-sync-summary">
              <div>
                <strong>외부 누락 감지</strong>
                <p>
                  {syncLoading
                    ? '외부 아파트 목록 확인 중...'
                    : syncCandidates.length > 0
                    ? `동기화 대기 ${syncCandidates.length}건`
                    : '현재 누락 감지 없음'}
                </p>
              </div>
              <div className="am-sync-actions">
                <button className="am-sync-btn" onClick={() => fetchSyncPreview()} disabled={syncLoading}>
                  다시 확인
                </button>
                <button
                  className="am-sync-all-btn"
                  onClick={() => syncApartmentCandidates()}
                  disabled={syncing || syncCandidates.length === 0}
                >
                  {syncing ? '동기화 중...' : '누락 아파트 자동추가'}
                </button>
              </div>
            </div>

            {syncCandidates.length > 0 && (
              <div className="am-sync-list">
                {syncCandidates.map((candidate) => (
                  <div key={`${candidate.apartment_name}-${candidate.apartment_id}`} className="am-sync-item">
                    <div className="am-sync-item-head">
                      <strong>{candidate.apartment_name}</strong>
                      <span className="am-sync-badge">
                        {candidate.is_configured ? '보정 필요' : '신규'}
                      </span>
                    </div>
                    <div className="am-sync-meta">
                      <span>아파트 코드: {candidate.apartment_id || '-'}</span>
                      {candidate.carryhealcount_name && (
                        <span>carryhealcount: {candidate.carryhealcount_name}</span>
                      )}
                      {candidate.admin_label && candidate.admin_label !== candidate.apartment_name && (
                        <span>관리자명: {candidate.admin_label}</span>
                      )}
                      {candidate.sync_source === 'carryhealcount_only' && (
                        <span>감지방식: carryhealcount 기준</span>
                      )}
                    </div>
                    {candidate.sync_warning && (
                      <div className="am-sync-warning">{candidate.sync_warning}</div>
                    )}
                    <div className="am-sync-item-actions">
                      <button className="am-sync-item-btn secondary" onClick={() => loadCandidateIntoForm(candidate)}>
                        폼에 불러오기
                      </button>
                      <button
                        className="am-sync-item-btn"
                        onClick={() => syncApartmentCandidates([candidate.apartment_name])}
                        disabled={syncing || !candidate.apartment_id}
                      >
                        {candidate.apartment_id ? '이 아파트 동기화' : '아파트 코드 확인 필요'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <input
              type="text"
              placeholder="아파트 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="am-input"
            />

            <div className="am-add-row">
              <input
                type="text"
                placeholder="새 아파트명"
                value={newApartmentName}
                onChange={(e) => setNewApartmentName(e.target.value)}
                className="am-input"
              />
              <button className="am-add-btn" onClick={addApartmentDraft}>
                추가
              </button>
            </div>

            <div className="am-apartment-list">
              {loading ? (
                <div className="am-muted">불러오는 중...</div>
              ) : filteredConfigs.length === 0 ? (
                <div className="am-muted">표시할 아파트가 없습니다.</div>
              ) : (
                filteredConfigs.map((item) => (
                  <button
                    key={item.apartment_name}
                    className={`am-apartment-item ${selectedName === item.apartment_name ? 'active' : ''}`}
                    onClick={() => selectApartment(item.apartment_name)}
                  >
                    <span className="am-apartment-name">{item.apartment_name}</span>
                    <span className="am-apartment-meta">
                      <span className="am-apartment-code">
                        코드: {item.apartment_id || '미설정'}
                      </span>
                      <span className={`am-dot ${item.is_configured ? 'on' : 'off'}`} />
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </aside>

        <main className="am-form-panel">
          <div className="am-card">
            <h2>설정 편집</h2>
            <div className="am-grid">
              <label>아파트명</label>
              <input
                type="text"
                value={form.apartment_name}
                onChange={(e) => setForm((prev) => ({ ...prev, apartment_name: e.target.value }))}
                className="am-input"
                placeholder="아파트명"
              />

              <label>아파트 코드</label>
              <input
                type="text"
                value={form.apartment_id || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, apartment_id: e.target.value }))}
                className="am-input"
                placeholder="예: gci, opf, shv"
              />
              <div className="am-field-help-spacer" />
              <p className="am-field-help">
                캐리힐 관리자 내일의배송 조회 URL의 apt 값입니다. 이 코드로 아파트별 차트를 자동 조회합니다.
              </p>

              <label>도로명 주소</label>
              <input
                type="text"
                value={form.apartment_address || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, apartment_address: e.target.value }))}
                className="am-input"
                placeholder="배송 차트용 주소"
              />

              <label>공동현관 비밀번호</label>
              <input
                type="text"
                value={form.entrance_password || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, entrance_password: e.target.value }))}
                className="am-input"
                placeholder="예: #1234"
              />
            </div>

            <div className="am-line-settings">
              <div className="am-line-header">
                <h3>동별 라인 설정</h3>
                <button className="am-add-line-btn" onClick={addLineRow}>
                  + 행 추가
                </button>
              </div>
              <p className="am-help">예시: 동 `101`, 라인 `1,2-3,4`</p>

              {form.line_settings.map((row, idx) => (
                <div className="am-line-row" key={`${idx}-${row.dong}`}>
                  <input
                    type="text"
                    value={row.dong}
                    onChange={(e) => updateLine(idx, 'dong', e.target.value)}
                    className="am-input"
                    placeholder="동"
                  />
                  <input
                    type="text"
                    value={row.line}
                    onChange={(e) => updateLine(idx, 'line', e.target.value)}
                    className="am-input"
                    placeholder="라인"
                  />
                  <button className="am-delete-line-btn" onClick={() => removeLineRow(idx)}>
                    삭제
                  </button>
                </div>
              ))}
            </div>

            <div className="am-actions">
              <button className="am-save-btn" onClick={saveConfig} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
              <button className="am-delete-btn" onClick={deleteConfig} disabled={!form.apartment_name.trim()}>
                아파트 설정 삭제
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ApartmentManager;
